// Ad Preview Mockups Stage 1 §1.2. Pure helper — Drive filename → AdType.
//
// Rule (case-insensitive):
//   contains FBS as a standalone token → 'story'
//   contains FBA as a standalone token → 'feed'
//   else                               → 'feed' (back-up default)
//
// "Standalone token" means the FBA / FBS letters are bounded on BOTH sides by
// a non-letter — a digit, space, dot, dash, underscore, OR the start / end of
// the string. Only a Latin letter fails to bound. So:
//   - `_FBS_` / `_FBA_` still match (underscore is a non-letter) — the original
//     underscore rule is preserved as a special case of this looser rule.
//   - `campaign123FBS` → 'story', `file123FBA` → 'feed', `Hero Shot FBA` → 'feed'
//     (digit / space boundaries now count, not just underscores).
//   - `myFBAfile` does NOT match (FBA is embedded inside a word — letters on
//     both sides), so it falls through to the 'feed' default.
//
// Every asset gets a label: a filename with no FBA / FBS token defaults to
// 'feed' rather than null, so no asset is ever left unclassified. The function
// therefore never returns null now (the AdType union still permits it for
// callers / legacy stored values).
//
// Used at label-creation time by DriveAssetGrid.upsertLabel so a freshly-
// authored label auto-populates its adType. An AM can still override the
// detected value from the AdTypeDropdown; explicit 'story' / 'feed' picks are
// preserved on re-detection (DriveAssetGrid only re-detects when adType == null
// — and because detection now always yields a value, a null/undefined adType
// resolves to the detected token or 'feed').
//
// Story is checked before feed so a (rare, defensive) filename carrying both
// tokens resolves to 'story'.

import type { AdType } from '../types/campaign.js';

// Token bounded by a non-letter (or string edge) on each side. With the `i`
// flag the negative look-around classes cover both cases. Lookbehind is
// ES2018 — supported by every browser this internal tool targets + Node.
const STORY_TOKEN = /(?<![A-Za-z])fbs(?![A-Za-z])/i;
const FEED_TOKEN = /(?<![A-Za-z])fba(?![A-Za-z])/i;

export function detectAdTypeFromFilename(fileName: string): AdType {
  if (STORY_TOKEN.test(fileName)) return 'story';
  if (FEED_TOKEN.test(fileName)) return 'feed';
  return 'feed';
}
